import React, { useEffect, useRef, useState, useCallback } from 'react'
import { TestimonialCarouselProps } from './TestimonialCarousel.types'
import { getValueListBgColor } from '../../../utils/background-util'
import { RTECompound } from '../../Compounds/src/RTECompound'
import { TestimonialCard } from './TestimonialCard'

export const TestimonialCarousel = (props: TestimonialCarouselProps) => {
  const {
    backgroundColor,
    testimonialCardList = [],
    headline,
    introduction,
    viewType = 'single-bubble',
    leftArrowDataAnalytics,
    rightArrowDataAnalytics,
  } = props.content

  const [position, setPosition] = useState(0)
  const [positionOffset, setPositionOffset] = useState(0)
  const [cardWidth, setCardWidth] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const testimonialCarouselComponentRef = useRef<HTMLDivElement>(null)
  const [startY, setStartY] = useState(0)
  const [startX, setStartX] = useState(0)
  const CARD_GAP = 20
  const CARD_PEEK = cardWidth === 325 && position > 0 ? 25 : 0

  const testimonialCardListData = Array.isArray(testimonialCardList)
    ? testimonialCardList
    : [testimonialCardList]

  const moveLeft = useCallback(
    (jumpAmount: number) => {
      if (position === 0) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position - 1)
        return
      }
      if (position - jumpAmount <= 0) {
        setPosition(0)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position]
  )
  const moveRight = useCallback(
    (jumpAmount: number) => {
      if (!testimonialCardListData || position === testimonialCardListData.length - 1) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position + 1)
        return
      }
      if (position - jumpAmount >= testimonialCardListData.length) {
        setPosition(testimonialCardListData.length - 1)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position, testimonialCardListData]
  )
  const onSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaY) > 100) {
        return
      }
      const numCardsMoved = deltaX / (cardWidth + CARD_GAP)
      if (deltaX > 50) {
        moveLeft(Math.floor(numCardsMoved))
      }
      if (deltaX < -50) {
        moveRight(Math.ceil(numCardsMoved))
      }
    },
    [moveLeft, moveRight, cardWidth]
  )
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
      return
    }
    e.preventDefault()
    setIsSwiping(true)
    setStartX(e.touches[0].clientX)
    setStartY(e.touches[0].clientY)
  }, [])

  const handleTouch = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const deltaX = endX - startX
      setPositionOffset(deltaX)
    },
    [startX, setPositionOffset]
  )

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      const deltaX = endX - startX
      const deltaY = endY - startY
      onSwipe(deltaX, deltaY)
      setPositionOffset(0)
      setIsSwiping(false)
    },
    [startX, startY, onSwipe]
  )

  const updateHeight = () => {
    if (testimonialCarouselComponentRef.current) {
      if (window.matchMedia('screen and (max-width:1024px)').matches) {
        testimonialCarouselComponentRef.current.style.minHeight = 'auto'
      } else {
        testimonialCarouselComponentRef.current.style.minHeight = `${
          (wrapperRef.current?.offsetHeight || 0) + 130
        }px`
      }
    }
  }

  useEffect(() => {
    updateHeight()
  }, [wrapperRef.current?.offsetHeight])

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchmove', handleTouch)
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouch)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchEnd, handleTouch])

  const handleWindowSizeChange = () => {
    if (viewType === 'multi-bubble') {
      setCardWidth(325)
    } else if (window.matchMedia('screen and (max-width:1024px)').matches) {
      setCardWidth(325)
    } else {
      setCardWidth(750)
    }
  }

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])
  if (!testimonialCardListData) {
    return null
  }
  const gridColumnWidth = `${cardWidth}px`
  const gridStyle = {
    gridTemplateColumns: `repeat(${testimonialCardListData.length}, ${gridColumnWidth})`,
    right: position * (cardWidth + CARD_GAP) - positionOffset - CARD_PEEK,
  }

  const renderCarouselControls = () => {
    return (
      <div className='card-carousel-controls u-flex u-flexAlignItemsEnd u-marginBottom1gu u-posRelative'>
        <button
          className='Button Button--secondary--action-blue Button--icon Button--icon--stroke'
          data-analytics={leftArrowDataAnalytics}
          onClick={() => moveLeft(0)}
          disabled={position === 0}>
          <span className='adel-material-symbols' aria-hidden='true' role='img'>
            chevron_left
          </span>
          <span className='u-hiddenVisually'>Slide Left</span>
        </button>
        <button
          className='Button Button--secondary--action-blue Button--icon Button--icon--stroke'
          data-analytics={rightArrowDataAnalytics}
          onClick={() => moveRight(0)}
          disabled={position === testimonialCardListData.length - 1}>
          <span className='adel-material-symbols' aria-hidden='true' role='img'>
            chevron_right
          </span>
          <span className='u-hiddenVisually'>Slide Right</span>
        </button>
        <p className='u-textColorActionBlue'>
          {position + 1} / {testimonialCardListData.length}
        </p>
        <div className='u-hiddenVisually' id='announce-slide' aria-live='polite'>
          {`You are currently on slide ${position + 1} of ${testimonialCardListData.length}`}
        </div>
      </div>
    )
  }

  return (
    <section
      className={`testimonial-carousel-component u-flex u-sm-paddingVert10gu ${getValueListBgColor(
        backgroundColor || 'white'
      )}`}
      ref={testimonialCarouselComponentRef}
      aria-labelledby='testimonial-carousel-heading'>
      <div className='testimonial-carousel-container u-sizeConstrained u-flex u-sm-flexCol u-posRelative'>
        <div className='testimonial-carousel-description u-sm-sizeFull u-flex u-flexAlignItemsCenter u-paddingRight8gu u-sm-paddingHoriz4gu u-sm-marginBottom6gu'>
          <div className='u-sizeFull'>
            <h3 id='testimonial-carousel-heading' className='u-textColorMidnightBlue u-textBold'>
              {headline}
            </h3>
            {introduction && (
              <div className='u-marginTop4gu'>
                <RTECompound {...introduction} />
              </div>
            )}
            <div className='u-sm-hidden u-marginTop4gu'>{renderCarouselControls()}</div>
          </div>
        </div>
        <div
          className={`testimonial-carousel--overflowWrapper u-sm-sizeFull u-paddingHoriz4gu ${
            viewType === 'multi-bubble' ? 'multi-bubble' : 'single-bubble'
          }`}>
          <div
            className={`testimonial-card-list-wrapper u-posRelative ${
              isSwiping ? 'testimonial-card-list-wrapper--noTransition' : ''
            }`}
            style={gridStyle}
            ref={wrapperRef}
            data-testid='testimonial-card-list-wrapper'>
            {testimonialCardListData.map((card, index) => (
              <div
                key={`${card.personInfo.content}_${index}`}
                className='u-flex'
                aria-current={index === position}
                aria-hidden={index !== position}>
                <TestimonialCard {...{ ...card, viewType }} />
              </div>
            ))}
          </div>
        </div>
        <div className='u-lg-hidden u-md-hidden u-sm-marginTop5gu u-sm-paddingTop5gu u-sm-paddingHoriz4gu'>
          {renderCarouselControls()}
        </div>
      </div>
    </section>
  )
}
